// src/pages/Campaigns/VolunteerManagement/OutreachCampaignWizard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Stepper, Step, StepLabel, Paper,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  TextField, Checkbox, List, ListItem, ListItemText, ListItemIcon,
  Chip, Alert, CircularProgress, Grid, Card, CardContent, Snackbar, IconButton,
  Pagination, InputAdornment, Stack
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import {
  PreferredTimeSlotLabels,
  EventTypeLabels
} from './volunteerEnums';
import { CheckCircle as CheckCircleIcon, Search as SearchIcon } from '@mui/icons-material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';


const OutreachCampaignWizard = () => {
  const { chatbotId, eventId } = useParams();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [selectedVolunteers, setSelectedVolunteers] = useState([]);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [checkingGmail, setCheckingGmail] = useState(true);
  
  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVolunteers, setTotalVolunteers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllVolunteers, setSelectAllVolunteers] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  
  const [campaignData, setCampaignData] = useState({
    campaign_name: '',
    send_email: false,
    send_sms: false,
    email_subject: '',
    email_body: '',
    sms_message: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const steps = ['Select Volunteers', 'Choose Channels & Compose Message', 'Review & Send'];

  useEffect(() => {
    loadEvent();
    checkGmailConnection();
  }, [eventId, chatbotId]);

  // Load volunteers when page or search changes
  useEffect(() => {
    if (activeStep === 0) {
      // Debounce search
      const timeoutId = setTimeout(() => {
        loadVolunteers();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [page, searchTerm, activeStep]);

  // Check if all volunteers on current page are selected
  useEffect(() => {
    if (volunteers.length > 0) {
      const allCurrentPageSelected = volunteers.every(v => selectedVolunteers.includes(v.id));
      setSelectAll(allCurrentPageSelected);
    }
  }, [selectedVolunteers, volunteers]);

  // Check if all volunteers (across all pages) are selected
  useEffect(() => {
    if (totalVolunteers > 0 && selectedVolunteers.length === totalVolunteers) {
      setSelectAllVolunteers(true);
    } else {
      setSelectAllVolunteers(false);
    }
  }, [selectedVolunteers, totalVolunteers]);

  // Check for successful Gmail connection after OAuth redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      setGmailConnected(true);
      
      const recheckConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));
        await checkGmailConnection();
        
        setTimeout(async () => {
          const response = await axiosInstance.get(`/api/event-gmail/status`, {
            params: { chatbot_id: chatbotId }
          });
          if (!response.data.connected) {
            console.error('Gmail connection check failed:', response.data);
          }
          setGmailConnected(response.data.connected);
        }, 2000);
      };
      
      recheckConnection();
      window.history.replaceState({}, '', window.location.pathname);
      alert('Gmail connected successfully!');
    }
  }, []);

  const checkGmailConnection = async () => {
    setCheckingGmail(true);
    try {
      const response = await axiosInstance.get(`/api/event-gmail/status`, {
        params: { chatbot_id: chatbotId }
      });
      
      console.log('Gmail status check response:', response.data);
      
      if (response.data.debug) {
        console.error('Gmail connection issue:', response.data.debug);
      }
      
      setGmailConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking Gmail connection:', error);
      setGmailConnected(false);
    } finally {
      setCheckingGmail(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await axiosInstance.get(`/api/event-gmail/auth/start`, {
        params: { 
          chatbot_id: chatbotId,
          event_id: eventId
        }
      });
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else if (response.data.already_connected) {
        alert('Gmail is already connected');
        setGmailConnected(true);
      }
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      alert('Failed to connect Gmail. Please try again.');
    }
  };

  const loadEvent = async () => {
    try {
      const response = await axiosInstance.get(`/api/events`, {
        params: { chatbot_id: chatbotId }
      });
      const foundEvent = response.data.data.find(e => e.id === parseInt(eventId));
      setEvent(foundEvent);
      if (foundEvent) {
        setCampaignData(prev => ({
          ...prev,
          campaign_name: `Campaign for ${foundEvent.name}`,
          email_subject: `Volunteer Opportunity: ${foundEvent.name}`,
          email_body: `Hello,\n\nWe need your help for our ${foundEvent.name} event on ${foundEvent.event_date}.\n\nPlease confirm your availability using the link below.\n\nThank you!`,
          sms_message: `Volunteer opportunity for ${foundEvent.name} on ${foundEvent.event_date}. Please confirm your availability.`
        }));
      }
    } catch (error) {
      console.error('Error loading event:', error);
    }
  };

  const loadVolunteers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/volunteers`, {
        params: { 
          chatbot_id: chatbotId, 
          page: page,
          per_page: 10,
          search: searchTerm
        }
      });
      setVolunteers(response.data.data);
      setTotalPages(response.data.pagination.pages);
      setTotalVolunteers(response.data.pagination.total);
    } catch (error) {
      console.error('Error loading volunteers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVolunteerToggle = (volunteerId) => {
    setSelectedVolunteers(prev => {
      if (prev.includes(volunteerId)) {
        return prev.filter(id => id !== volunteerId);
      } else {
        return [...prev, volunteerId];
      }
    });
  };

  // Handle select all on current page
  const handleSelectAllToggle = () => {
    if (selectAll) {
      // Deselect all on current page
      const currentPageVolunteerIds = volunteers.map(v => v.id);
      setSelectedVolunteers(prev => prev.filter(id => !currentPageVolunteerIds.includes(id)));
      setSelectAll(false);
    } else {
      // Select all on current page
      const currentPageVolunteerIds = volunteers.map(v => v.id);
      setSelectedVolunteers(prev => {
        const newSelected = [...prev];
        currentPageVolunteerIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
      setSelectAll(true);
    }
  };

  // Handle select ALL volunteers (across all pages)
  const handleSelectAllVolunteersToggle = async () => {
    if (selectAllVolunteers) {
      // Deselect all volunteers
      setSelectedVolunteers([]);
      setSelectAllVolunteers(false);
    } else {
      // Select all volunteers
      setSelectingAll(true);
      try {
        // Fetch all volunteers without pagination
        const response = await axiosInstance.get(`/api/volunteers`, {
          params: { 
            chatbot_id: chatbotId, 
            per_page: 1000, // Large number to get all volunteers
            search: searchTerm
          }
        });
        
        const allVolunteerIds = response.data.data.map(v => v.id);
        setSelectedVolunteers(allVolunteerIds);
        setSelectAllVolunteers(true);
        
        // Show success message
        setSnackbar({
          open: true,
          message: `Selected all ${allVolunteerIds.length} volunteers`,
          severity: 'success'
        });
      } catch (error) {
        console.error('Error loading all volunteers:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load all volunteers',
          severity: 'error'
        });
      } finally {
        setSelectingAll(false);
      }
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedVolunteers.length === 0) {
      alert('Please select at least one volunteer');
      return;
    }
    if (activeStep === 1) {
      if (!campaignData.send_email && !campaignData.send_sms) {
        alert('Please select at least one communication channel');
        return;
      }
      if (campaignData.send_email && !gmailConnected) {
        alert('Gmail is not connected. Please connect your Gmail account first.');
        return;
      }
      if (campaignData.send_email && (!campaignData.email_subject || !campaignData.email_body)) {
        alert('Please provide email subject and body');
        return;
      }
      if (campaignData.send_sms && !campaignData.sms_message) {
        alert('Please provide SMS message');
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSendCampaign = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/api/outreach/campaign`, {
        event_id: parseInt(eventId),
        volunteer_ids: selectedVolunteers,
        ...campaignData
      }, {
        params: { chatbot_id: chatbotId }
      });
      
      setSnackbar({
        open: true,
        message: `Campaign sent successfully! ${response.data.data.total_sent} emails sent to volunteers.`,
        severity: 'success'
      });
      
      setTimeout(() => {
        navigate(`/chatbot/${chatbotId}/campaigns/events`);
      }, 2000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error sending campaign';
      
      if (errorMsg.includes('Gmail') || errorMsg.includes('not authenticated') || errorMsg.includes('token')) {
        setSnackbar({
          open: true,
          message: 'Gmail connection error. Redirecting to connect Gmail...',
          severity: 'error'
        });
        setTimeout(() => {
          navigate(`/chatbot/${chatbotId}/campaigns/Gmail`);
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: errorMsg,
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Volunteers for {event?.name}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select volunteers to invite for this event. They will receive a personalized link to confirm their availability.
            </Alert>
            
            {/* Search and Select All Controls */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, email, or phone"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(1); // Reset to first page on search
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllToggle}
                        disabled={volunteers.length === 0}
                      />
                    }
                    label="Select All on This Page"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAllVolunteers}
                        onChange={handleSelectAllVolunteersToggle}
                        disabled={totalVolunteers === 0 || selectingAll}
                      />
                    }
                    label={
                      selectingAll ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          Selecting...
                        </Box>
                      ) : (
                        `Select All (${totalVolunteers})`
                      )
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Volunteers List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : volunteers.length === 0 ? (
              <Alert severity="warning">
                No volunteers found. {searchTerm && 'Try adjusting your search.'}
              </Alert>
            ) : (
              <>
                <List>
                  {volunteers.map(volunteer => (
                    <ListItem
                      key={volunteer.id}
                      button
                      onClick={() => handleVolunteerToggle(volunteer.id)}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedVolunteers.includes(volunteer.id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={volunteer.full_name}
                        secondary={`${volunteer.email} | ${volunteer.phone_number}`}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, newPage) => setPage(newPage)}
                      color="primary"
                      sx={{
                        '& .Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: '#fff !important',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Selected: {selectedVolunteers.length} volunteer(s)
              {totalVolunteers > 0 && ` out of ${totalVolunteers} total`}
            </Typography>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose Communication Channels
            </Typography>
            
            {/* Gmail Connection Warning */}
            {checkingGmail ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Checking Gmail connection...
              </Alert>
            ) : !gmailConnected ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Gmail Not Connected</strong><br/>
                You need to connect your Gmail account to send emails. 
                <Button 
                  size="small" 
                  variant="outlined" 
                  sx={{ ml: 2 }}
                  onClick={handleConnectGmail}
                >
                  Connect Gmail
                </Button>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                Gmail is connected and ready to send emails.
              </Alert>
            )}
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel>Select Channel(s)</FormLabel>
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignData.send_email}
                      onChange={(e) => setCampaignData({
                        ...campaignData,
                        send_email: e.target.checked
                      })}
                      disabled={!gmailConnected}
                    />
                  }
                  label={
                    <Box>
                      Send Email
                      {!gmailConnected && (
                        <Chip 
                          label="Gmail Not Connected" 
                          size="small" 
                          color="warning" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignData.send_sms}
                      onChange={(e) => setCampaignData({
                        ...campaignData,
                        send_sms: e.target.checked
                      })}
                    />
                  }
                  label="Send SMS"
                />
              </Box>
            </FormControl>

            {campaignData.send_email && gmailConnected && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    Email Message
                  </Typography>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={campaignData.email_subject}
                    onChange={(e) => setCampaignData({
                      ...campaignData,
                      email_subject: e.target.value
                    })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Email Body"
                    value={campaignData.email_body}
                    onChange={(e) => setCampaignData({
                      ...campaignData,
                      email_body: e.target.value
                    })}
                    helperText="A personalized link will be automatically added to the email"
                  />
                </CardContent>
              </Card>
            )}

            {campaignData.send_sms && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    SMS Message
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="SMS Text"
                    value={campaignData.sms_message}
                    onChange={(e) => setCampaignData({
                      ...campaignData,
                      sms_message: e.target.value
                    })}
                    helperText="A personalized link will be automatically added. Keep message concise for SMS."
                    inputProps={{ maxLength: 160 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {campaignData.sms_message.length}/160 characters
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Campaign
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Event
                    </Typography>
                    <Typography variant="h6">{event?.name}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Recipients
                    </Typography>
                    <Typography variant="h6">{selectedVolunteers.length} volunteers</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Channels
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {campaignData.send_email && <Chip label="Email" size="small" sx={{ mr: 1 }} />}
                      {campaignData.send_sms && <Chip label="SMS" size="small" />}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {campaignData.send_email && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Email Preview
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        Subject: {campaignData.email_subject}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {campaignData.email_body}
                      </Typography>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        A unique confirmation link will be added for each volunteer
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {campaignData.send_sms && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        SMS Preview
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {campaignData.sms_message}
                      </Typography>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        A unique confirmation link will be added for each volunteer
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (!event) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={700}>
          Create Outreach Campaign
        </Typography>
      </Box>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            <Button
              onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSendCampaign}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Campaign'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OutreachCampaignWizard;